import {
  Box,
  Card,
  FormControl,
  Grid2,
  Paper,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { ControlledDateFormField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-date-field";
import { ControlledTextFormField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-text-field";
import { usePimoForm } from "@pimo/pimo-components/src/lib/pimo-components/use-pimo-form/use-pimo-form.hook";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import dayjs from "dayjs";
import { useState } from "react";
import { Configuration, ExtendedFieldError, ReportingDetails } from "vmd-types";

import { getDateInputFormat } from "../utils/format-date";
import { CustomButton } from "./custom-fields/custom-button";

export type ReportingPeriodProps = {
  reportingDetails?: ReportingDetails;
  language?: SupportedLanguage;
};

export const ReportingPeriodPage: PimoReactComponent<
  ReportingPeriodProps,
  "update:reporting-period",
  ReportingDetails
> = ({ language = "en" }) => {
  const dateInputFormat = getDateInputFormat(language);
  const { setValue, formState } = usePimoForm<{
    configuration: Configuration;
    validationErrors: Record<string, ExtendedFieldError>;
    enableSaveButton?: boolean;
  }>({
    appStateSlice: "editSettingsForm",
  });
  const [selectedOption, setSelectedOption] = useState<
    "week" | "month" | "year" | undefined
  >(formState?.configuration?.reportingCycle?.unit ?? "week");

  // Sync external form state into local state during render
  // instead of via a useEffect to avoid cascading renders.
  const reportingCycleUnit = formState?.configuration?.reportingCycle?.unit;
  const [previousReportingCycleUnit, setPreviousReportingCycleUnit] =
    useState(reportingCycleUnit);
  if (reportingCycleUnit !== previousReportingCycleUnit) {
    setPreviousReportingCycleUnit(reportingCycleUnit);
    setSelectedOption(reportingCycleUnit);
  }
  return (
    <Card
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        p: 2,
        boxShadow: "none",
        gap: 2,
        height: "calc(100vh - 83px)", // Adjust height to fit below the header
      }}
    >
      <Typography
        sx={{
          fontSize: "24px",
          fontWeight: 500,
        }}
      >
        {getLocalizedMessage(language || "en", "reportingPeriod.title") ||
          "Reporting Period"}
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
          color: grey[600],
        }}
      >
        {getLocalizedMessage(language || "en", "reportingPeriod.description") ||
          "With Pimo Reporting, you can define the reporting frequency and duration"}
      </Typography>
      <Paper
        sx={{
          p: 4,
          borderRadius: 3,
          boxShadow: "none",
          border: "1px solid",
          borderColor: grey[300],
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            textAlign: "left",
            width: "100%",
            margin: "auto",
          }}
        >
          {getLocalizedMessage(
            language || "en",
            "reportingPeriod.startEndDate"
          ) || "Start and End Date of the Report"}
          *
        </Typography>
        <Grid2 container spacing={2}>
          <Grid2 size={6}>
            <ControlledDateFormField
              inputFormat={dateInputFormat}
              value={
                formState?.configuration?.reportingCycle?.startReportingDate
              }
              error={Boolean(
                formState.validationErrors?.["startReportingDate"]
              )}
              helperText={
                formState.validationErrors?.["startReportingDate"]?.message
              }
              onChange={(value) => {
                setValue(
                  "configuration.reportingCycle.startReportingDate",
                  value
                );

                const newValidationErrors = {
                  ...(formState.validationErrors ?? {}),
                };

                if (!value) {
                  newValidationErrors["startReportingDate"] = {
                    type: "required",
                    message:
                      getLocalizedMessage(
                        language || "en",
                        "validationErrors.fieldRequired"
                      ) || "This field is required",
                    tabContainingErrors: `Reporting Period`,
                  };
                } else if (value.isValid() === false) {
                  newValidationErrors["startReportingDate"] = {
                    type: "invalid",
                    message:
                      getLocalizedMessage(
                        language || "en",
                        "validationErrors.dateNotValid"
                      ) || "This date is not valid",
                    tabContainingErrors: `Reporting Period`,
                  };
                } else {
                  delete newValidationErrors["startReportingDate"];
                }

                setValue("validationErrors", newValidationErrors);
                setValue("enableSaveButton", true);
              }}
            ></ControlledDateFormField>
          </Grid2>
          <Grid2 size={6}>
            <ControlledDateFormField
              inputFormat={dateInputFormat}
              value={formState?.configuration?.reportingCycle?.endReportingDate}
              error={Boolean(formState.validationErrors?.["endReportingDate"])}
              minDate={
                formState?.configuration?.reportingCycle?.startReportingDate ??
                undefined
              }
              helperText={
                formState.validationErrors?.["endReportingDate"]?.message
              }
              onChange={(value) => {
                setValue(
                  "configuration.reportingCycle.endReportingDate",
                  value
                );

                setValue("enableSaveButton", true);

                const newErrors = { ...(formState.validationErrors ?? {}) };

                let entry: ExtendedFieldError | undefined;

                if (!value) {
                  entry = {
                    type: "required",
                    message: "End date is required",
                    tabContainingErrors: "Reporting Period",
                  };
                } else if (value && value.isValid() === false) {
                  entry = {
                    type: "invalid",
                    message: "This is not a valid date",
                    tabContainingErrors: "Reporting Period",
                  };
                } else if (
                  value &&
                  formState.configuration.reportingCycle?.startReportingDate &&
                  value.isBefore(
                    dayjs(
                      formState.configuration.reportingCycle
                        ?.startReportingDate ?? ""
                    )
                  )
                ) {
                  entry = {
                    type: "invalid",
                    message:
                      getLocalizedMessage(
                        language || "en",
                        "validationErrors.endDateAfterStartDate"
                      ) || "End date should be after start date",
                    tabContainingErrors: "Reporting Period",
                  };
                }

                if (entry) {
                  newErrors["endReportingDate"] = entry;
                } else {
                  delete newErrors["endReportingDate"];
                }

                setValue("validationErrors", newErrors);
              }}
            ></ControlledDateFormField>
          </Grid2>
        </Grid2>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              textAlign: "left",
              width: "100%",
              margin: "auto",
            }}
          >
            {getLocalizedMessage(
              language || "en",
              "reportingPeriod.frequency"
            ) || "Frequency of the report"}
            *
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "row", gap: 5 }}>
            <CustomButton
              title={getLocalizedMessage(language || "en", "week") || "Week"}
              onClick={() => {
                setValue("configuration.reportingCycle.unit", "week");
                setValue("enableSaveButton", true);
                setSelectedOption("week");
                setValue(
                  "configuration.reportingCycle.weeklyReportingDay",
                  "0"
                );
              }}
              isActive={
                formState?.configuration?.reportingCycle?.unit === "week" ||
                selectedOption === "week"
              }
            />
            <CustomButton
              title={getLocalizedMessage(language || "en", "month") || "Month"}
              onClick={() => {
                setValue("configuration.reportingCycle.unit", "month");
                setSelectedOption("month");
              }}
              isActive={
                formState?.configuration?.reportingCycle?.unit === "month" ||
                selectedOption === "month"
              }
            />
            <CustomButton
              title={getLocalizedMessage(language || "en", "year") || "Year"}
              onClick={() => {
                setValue("configuration.reportingCycle.unit", "year");
                setSelectedOption("year");
              }}
              isActive={
                formState?.configuration?.reportingCycle?.unit === "year" ||
                selectedOption === "year"
              }
            />
          </Box>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              textAlign: "left",
              width: "100%",
              margin: "auto",
            }}
          >
            {getLocalizedMessage(
              language || "en",
              "reportingPeriod.recurrence"
            ) || "Reporting recurrence"}
            *
          </Typography>
          {selectedOption === "week" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignContent: "center",
                  alignItems: "center",
                  gap: 2,
                  fontSize: "14px",
                }}
              >
                <Typography sx={{ height: 50, lineHeight: 3 }}>
                  {getLocalizedMessage(
                    language || "en",
                    "reportingPeriod.recurEvery"
                  ) || "Recur every"}
                </Typography>
                <FormControl>
                  <ControlledTextFormField
                    error={Boolean(formState.validationErrors?.["frequency"])}
                    helperText={
                      formState.validationErrors?.["frequency"]?.message
                    }
                    isNumber={true}
                    value={
                      formState?.configuration?.reportingCycle?.frequency?.toString() ??
                      ""
                    }
                    onChange={(e) => {
                      setValue(
                        "configuration.reportingCycle.frequency",
                        Number(e)
                      );
                      const newErrors = {
                        ...(formState.validationErrors ?? {}),
                      };
                      if (!e) {
                        newErrors["frequency"] = {
                          type: "required",
                          message: "Frequency is required",
                          tabContainingErrors: "Reporting Period",
                        } as ExtendedFieldError;
                      } else {
                        delete newErrors["frequency"];
                      }
                      setValue("enableSaveButton", true);
                      setValue("validationErrors", newErrors);
                    }}
                  />
                </FormControl>
                <Typography sx={{ height: 50, lineHeight: 3 }}>
                  {getLocalizedMessage(
                    language || "en",
                    "reportingPeriod.weeksOn"
                  ) || "weeks(s) on:"}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  gap: 3,
                  flexWrap: "wrap",
                  width: "60%",
                }}
              >
                {[
                  getLocalizedMessage(language || "en", "sunday") || "Sunday",
                  getLocalizedMessage(language || "en", "monday") || "Monday",
                  getLocalizedMessage(language || "en", "tuesday") || "Tuesday",
                  getLocalizedMessage(language || "en", "wednesday") ||
                    "Wednesday",
                  getLocalizedMessage(language || "en", "thursday") ||
                    "Thursday",
                  getLocalizedMessage(language || "en", "friday") || "Friday",
                  getLocalizedMessage(language || "en", "saturday") ||
                    "Saturday",
                ].map((day, index) => (
                  <Box key={`${day}-${index}-container`}>
                    <CustomButton
                      title={day}
                      onClick={() => {
                        setValue("enableSaveButton", true);
                        setValue(
                          "configuration.reportingCycle.weeklyReportingDay",
                          index.toString()
                        );
                        setValue(
                          "configuration.reportingCycle.monthlyReportingDay",
                          null
                        );
                        setValue(
                          "configuration.reportingCycle.yearlyReportingDay",
                          null
                        );
                      }}
                      isActive={
                        formState?.configuration?.reportingCycle
                          ?.weeklyReportingDay === index.toString()
                      }
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {selectedOption === "month" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignContent: "center",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Typography sx={{ height: 50, lineHeight: 3 }}>
                  {getLocalizedMessage(
                    language || "en",
                    "reportingPeriod.alwaysRecursOn"
                  ) || "Always recurs on the"}
                </Typography>
                <FormControl>
                  <ControlledTextFormField
                    isNumber={true}
                    value={
                      formState?.configuration?.reportingCycle?.monthlyReportingDay?.toString() ??
                      ""
                    }
                    onChange={(e) => {
                      setValue("enableSaveButton", true);
                      setValue(
                        "configuration.reportingCycle.monthlyReportingDay",
                        e
                      );
                      setValue(
                        "configuration.reportingCycle.weeklyReportingDay",
                        null
                      );
                      setValue(
                        "configuration.reportingCycle.yearlyReportingDay",
                        null
                      );
                      const newErrors = {
                        ...(formState.validationErrors ?? {}),
                      };
                      if (
                        !e &&
                        formState?.configuration?.reportingCycle?.unit ===
                          "month"
                      ) {
                        newErrors["monthlyReportingDay"] = {
                          type: "required",
                          message: "This field is required",
                          tabContainingErrors: "Reporting Period",
                        } as ExtendedFieldError;
                      } else {
                        delete newErrors["monthlyReportingDay"];
                      }

                      setValue("validationErrors", newErrors);
                    }}
                    error={Boolean(
                      formState.validationErrors?.["monthlyReportingDay"]
                    )}
                    helperText={
                      formState.validationErrors?.["monthlyReportingDay"]
                        ?.message
                    }
                  />
                </FormControl>
                <Typography sx={{ height: 50, lineHeight: 3 }}>
                  {getLocalizedMessage(
                    language || "en",
                    "reportingPeriod.ofEvery"
                  ) || "of every"}
                </Typography>
                <FormControl>
                  <ControlledTextFormField
                    error={Boolean(formState.validationErrors?.["frequency"])}
                    helperText={
                      formState.validationErrors?.["frequency"]?.message
                    }
                    isNumber={true}
                    value={
                      formState?.configuration?.reportingCycle?.frequency?.toString() ??
                      ""
                    }
                    onChange={(e) => {
                      setValue("configuration.reportingCycle.frequency", e);
                      setValue("enableSaveButton", true);
                      const newErrors = {
                        ...(formState.validationErrors ?? {}),
                      };
                      if (
                        !e &&
                        formState?.configuration?.reportingCycle?.unit ===
                          "month"
                      ) {
                        newErrors["frequency"] = {
                          type: "required",
                          message:
                            getLocalizedMessage(
                              language || "en",
                              "validationErrors.fieldRequired"
                            ) || "This field is required",
                          tabContainingErrors: "Reporting Period",
                        } as ExtendedFieldError;
                      } else {
                        delete newErrors["frequency"];
                      }

                      setValue("validationErrors", newErrors);
                    }}
                  />
                </FormControl>
                <Typography sx={{ height: 50, lineHeight: 3 }}>
                  {getLocalizedMessage(
                    language || "en",
                    "reportingPeriod.months"
                  ) || "month(s)."}
                </Typography>
              </Box>
            </Box>
          )}
          {selectedOption === "year" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignContent: "center",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Typography sx={{ height: 50, lineHeight: 3 }}>
                  {getLocalizedMessage(
                    language || "en",
                    "reportingPeriod.recurEvery"
                  ) || "Recur every"}
                </Typography>
                <FormControl>
                  <ControlledTextFormField
                    isNumber={true}
                    error={Boolean(formState.validationErrors?.["frequency"])}
                    helperText={
                      formState.validationErrors?.["frequency"]?.message
                    }
                    value={
                      formState?.configuration?.reportingCycle?.frequency?.toString() ??
                      ""
                    }
                    onChange={(e) => {
                      setValue("enableSaveButton", true);
                      setValue(
                        "configuration.reportingCycle.frequency",
                        Number(e)
                      );
                      const newErrors = {
                        ...(formState.validationErrors ?? {}),
                      };
                      if (!e) {
                        newErrors["frequency"] = {
                          type: "required",
                          message:
                            getLocalizedMessage(
                              language || "en",
                              "validationErrors.fieldRequired"
                            ) || "This field is required",
                          tabContainingErrors: "Reporting Period",
                        } as ExtendedFieldError;
                      } else {
                        delete newErrors["frequency"];
                      }

                      setValue("validationErrors", newErrors);
                    }}
                  />
                </FormControl>
                <Typography sx={{ height: 50, lineHeight: 3 }}>
                  {getLocalizedMessage(
                    language || "en",
                    "reportingPeriod.yearsOn"
                  ) || "year(s) on:"}
                </Typography>
                <ControlledDateFormField
                  inputFormat={dateInputFormat}
                  value={
                    formState?.configuration?.reportingCycle?.yearlyReportingDay
                  }
                  error={Boolean(
                    formState.validationErrors?.["yearlyReportingDay"]
                  )}
                  helperText={
                    formState.validationErrors?.["yearlyReportingDay"]?.message
                  }
                  onChange={(value) => {
                    setValue("enableSaveButton", true);
                    setValue(
                      "configuration.reportingCycle.yearlyReportingDay",
                      value
                    );
                    setValue(
                      "configuration.reportingCycle.monthlyReportingDay",
                      null
                    );
                    setValue(
                      "configuration.reportingCycle.weeklyReportingDay",
                      null
                    );
                    const newErrors = {
                      ...(formState.validationErrors ?? {}),
                    };
                    if (!value) {
                      newErrors["yearlyReportingDay"] = {
                        type: "required",
                        message:
                          getLocalizedMessage(
                            language || "en",
                            "validationErrors.fieldRequired"
                          ) || "This field is required",
                        tabContainingErrors: "Reporting Period",
                      } as ExtendedFieldError;
                    } else {
                      delete newErrors["yearlyReportingDay"];
                    }
                    setValue("validationErrors", newErrors);
                  }}
                ></ControlledDateFormField>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Card>
  );
};
